import React, { useState, useCallback, useMemo, useEffect } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { useClient } from 'shared/apollo';
import { Field } from 'shared/containers/form';
import ImagePicker, { ImageUploadButton } from 'shared/components/ImagePicker';
import { ConfirmationDialog } from 'shared/components';
import { signOut } from 'shared/auth';
import useAccount from './useAccount';
import {
  Form, TextField, Submit,
} from '../../components/styled';
import {
  Root, Container, Body,
  Header, Button, ButtonBar, RowSpaceBetween, Divider,
  FieldRow
} from './styled';
import AddressInput from 'shared/components/AddressInput';
import { accountMutation, logoMutation, deleteLogoMutation } from './mutations';
import { Warning } from 'npo/pages/Reports/styled';
import { validateEINFormat } from './validateEIN';

export default function Organization() {
  window.localStorage.removeItem('newLocation');
  const account = useAccount();

  const [isNonUs, setIsNonUs] = useState(false);
  const handleChangeNonUs = useCallback(event => {
    const { checked } = event.target;
    setIsNonUs(checked);
  }, [setIsNonUs]);
  const client = useClient();
  const [fileToUpload, setFileToUpload] = useState(null);
  const [initialLogo, setInitialLogo] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [formError, setFormError] = useState();
  const [einError, setEinError] = useState();

  useEffect(() => {
    if (account?.logoFile) {
      setInitialLogo(account?.logoFile);
    }
    if (account?.id) {
      setFormValues({
        ...formValues,
        woman: account?.woman,
        personOfColor: account?.personOfColor
      });
    }
    if (account?.ein && account.ein !== '000000000') {
      setIsNonUs(false);
    } else if (account?.ein === '000000000') {
      setIsNonUs(true);
    }
  }, [account]);

  const handleSubmit = useCallback(async ({ noEin, logo, ...rest }) => {
    const values = {
      ...rest,
      ...(isNonUs ? { ein: '000000000' } : null),
    };

    if (!isNonUs && values.ein) {
      const validation = validateEINFormat(values.ein);
      if (!validation.isValid) {
        setEinError(validation.error);
        setFormError(validation.error);
        return;
      }
    }
    setEinError(undefined);

    values.numberOfWomen = parseInt(values.numberOfWomen);
    values.numberOfpeopleOfColor = parseInt(values.numberOfpeopleOfColor);
    values.totalNumber = parseInt(values.totalNumber);

    if (values.numberOfWomen > values.totalNumber) {
      setFormError('The total \'Number of women\' must not exceed the \'Total number\'.');
      return;
    } else if (values.numberOfpeopleOfColor > values.totalNumber) {
      setFormError('The total \'People of color\' must not exceed the \'Total number\'.');
      return;
    }
    setFormError(undefined);

    const locationRaw = window.localStorage.getItem('newLocation');
    const location = (locationRaw) ? JSON.parse(locationRaw) : null;

    const result = await client.mutate({
      mutation: accountMutation,
      variables: {
        ...values,
        ...(location ? {
          locationLat: location.lat,
          locationLng: location.lng,
        } : null),
      }
    }).catch((error) => {
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Unable to save the changes, please try again.';
      if (errorMessage.includes('EIN')) {
        setEinError(errorMessage);
      }
      setFormError(errorMessage);
      return null;
    });

    if (result?.data?.myAccountSet?.error) {
      const errorMessage = result.data.myAccountSet.error;
      if (errorMessage.includes('EIN')) {
        setEinError(errorMessage);
      }
      setFormError(errorMessage);
      return;
    }

    if (fileToUpload) {
      const logo = await fileToUpload();
      if (logo) {
        const result2 = await client.mutate({
          mutation: logoMutation,
          variables: {
            type: 'account_logo',
            logo,
          }
        });
      }
    }
  }, [isNonUs, client, fileToUpload, setFormError]);

  const handleLatLng = (newLocation) => {
    window.localStorage.setItem('newLocation', JSON.stringify(newLocation));
  };
  const [newLogoUrl, setNewLogoUrl] = useState();
  const handleUpload = useCallback(({ target: { value } }) => {
    const { url, file } = value;
    setNewLogoUrl(url);
  }, []);
  const handleDeleteLogo = async () => {
    setNewLogoUrl(null);
    setInitialLogo(null);
    await client.mutate({
      mutation: deleteLogoMutation,
      variables: { id: account.id }
    });
  };
  const handleFile = file => {
    setFileToUpload(file);
  };
  const [formValues, setFormValues] = useState({});
  const handleValidate = useCallback(values => {
    setImmediate(() => setFormValues(values));
    return true;
  }, []);
  const getPercentage = (value) => {
    const total = parseInt(formValues.totalNumber || account?.totalNumber);
    const percentage = total && value ? Math.round(parseInt(value) * 100 / total) : '';
    if (isNaN(percentage) || (percentage > 100) || (percentage < 0)) return '';
    return `${percentage}%`;
  };
  const accountValues = account;
  const initialValues = useMemo(() => ({
    ...accountValues,
    noEin: '000000000',
  }), [accountValues]);

  const handleShowDeleteDialog = () => {
    setDeleteDialog(true);
  };

  const handleDeleteAccount = () => {
    setDeleteDialog(false);
    signOut();
  };

  if (!account) {
    return <LinearProgress variant="query" />;
  }

  const percentageWomen = getPercentage(formValues.numberOfWomen || account?.numberOfWomen);
  const percentagePeople = getPercentage(formValues.numberOfpeopleOfColor || account?.numberOfpeopleOfColor);

  //  const { userAttributes, handleSubmit, handleEmailVerified } = useUserAttributes()
  return (<Container>
    <Form
      {...{ initialValues }}
      onSubmit={handleSubmit}
      onValidate={handleValidate}
    >
      <Header>
        <h2>My Organization</h2>
        <ButtonBar>
          <ImagePicker
            value={newLogoUrl ?? initialLogo}
            forceDirty={!!newLogoUrl}
            onFile={handleFile}
            size={50}
            style={{ display: 'flex' }}
          >
            <b>{accountValues?.name}</b>
            <Field name="fileToUpload" onChange={handleUpload} component={ImageUploadButton}>
              <Button component="span">
                Upload New Picture
              </Button>
            </Field>
          </ImagePicker>
          <Button variant="outlined" onClick={handleDeleteLogo}>
            Delete Picture
          </Button>
        </ButtonBar>
      </Header>
      <Body>
        <Root maxWidth={800}>
          <h2>General Information</h2>
          <label>
            Organization name:<br />
            <Field
              name="name"
              placeholder="Origanization name"
              required
              component={TextField}
              fullWidth
            />
          </label>
          <br /><br />
          <label>
            TIN / EIN:<br />
            <Field
              name={isNonUs ? 'noEin' : 'ein'}
              placeholder="TIN / EIN"
              required={!isNonUs}
              component={TextField}
              fullWidth
            />
          </label>
          <div><i>Taxpayer Identification Number / Employer Identification Number</i></div>
          {einError && <Warning>{einError}</Warning>}
          <br />
          <FormControlLabel
            control={<Checkbox checked={isNonUs} onChange={handleChangeNonUs} />}
            label="My organization doesn't have an EIN (ex: for non-US based organizations)"
          />
          <br /><br />
          <label>
            <RowSpaceBetween>
              <div>Origanization website:</div>
              <div class="optional">(optional)</div>
            </RowSpaceBetween>
            <Field
              name="website"
              placeholder="Origanization website"
              component={TextField}
              fullWidth
            />
          </label>
          <br /><br />
          <label>
            Mission statement:<br />
            <Field
              name="missionStatement"
              placeholder="Mission statement"
              required
              multiline
              rows={4}
              maxRows={4}
              fullWidth
              component={TextField}
            />
          </label>
          <br /><br />
          <label>
            Office location:
            <Field
              name="officeLocation"
              required
              component={AddressInput}
              onLatLng={handleLatLng}
            />
          </label>
          <br />
          {/* <Divider />
          <RowSpaceBetween>
            <h2>Diversity & Inclusion</h2>
            <div class="optional tt10">(optional)</div>
          </RowSpaceBetween>
          <h3>Executive director/chief executive:</h3>
          <Field
            label="Woman"
            name="woman"
            component={CheckboxWithLabel}
            checked={formValues?.woman}
          />
          <br />
          <Field
            label="Person of color"
            name="personOfColor"
            component={CheckboxWithLabel}
            checked={formValues?.personOfColor}
          />
          <br /><br />
          <h3>Senior leadership</h3>
          <br />
          <label>
            Number of women:<br />
            <FieldRow>
              <Field
                name="numberOfWomen"
                placeholder="Number of women"
                component={TextField}
              />
              <span>{percentageWomen}</span>
            </FieldRow>
          </label>
          <br />
          <label>
            People of color:<br />
            <FieldRow>
              <Field
                name="numberOfpeopleOfColor"
                placeholder="People of color"
                component={TextField}
              />
              <span>{percentagePeople}</span>
            </FieldRow>
          </label>
          <br />
          <label>
            Total number:<br />
            <FieldRow>
              <Field
                name="totalNumber"
                placeholder="Number of women"
                component={TextField}
              />
            </FieldRow>
          </label>
          <br />
          <Divider /> */}
          {formError && <Warning>{formError}</Warning>}
          <br />
          <Submit fullWidth>
            Save All Changes
          </Submit>
          <br /><br />
          <Divider />
          <br />
          {/*<Button fullWidth variant="outlined" onClick={handleShowDeleteDialog}>
            Delete This Account
          </Button>
          <br/><br/>*/}
        </Root>
      </Body>
    </Form>
    <ConfirmationDialog
      header={'Delete Account'}
      openDialog={deleteDialog}
      onCloseDialog={() => setDeleteDialog(false)}
      actionButtonOne={handleDeleteAccount}
      actionButtonTwo={() => setDeleteDialog(false)}
      textButtonOne="Delete"
      textButtonTwo="Cancel"
      title={'Please confirm you want to delete this account. This action is permanent.'}
    />
  </Container>);
}

const CheckboxWithLabel = props => (
  <FormControlLabel
    control={<Checkbox />}
    {...props}
  />
);
